/* eslint-disable no-restricted-globals */
import React, { useState, useEffect } from 'react';
import { Calendar, Mail, Phone, MapPin, User, Sparkles, CheckCircle, AlertCircle } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://your-project.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your-anon-key';
const supabase = createClient(supabaseUrl, supabaseKey);

const CHRIS_USER_ID = process.env.REACT_APP_CHRIS_USER_ID || 'your-user-id-here';

const PublicBookingForm = () => {
  const [formData, setFormData] = useState({
    clientName: '',
    email: '',
    phone: '',
    eventDate: '',
    eventType: '',
    venue: '',
    estimatedBudget: '',
    notes: ''
  });

  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    trackEvent('page_view', {
      page: 'booking_form',
      user_agent: navigator.userAgent,
      referrer: document.referrer
    });

    return () => {
      const timeOnPage = Math.round((Date.now() - startTime) / 1000);
      trackEvent('page_exit', {
        page: 'booking_form',
        time_on_page_seconds: timeOnPage
      });
    };
  }, [startTime]);

  const trackEvent = async (eventName, eventData = {}) => {
    try {
      await supabase.from('analytics_events').insert([{
        user_id: CHRIS_USER_ID,
        session_id: sessionId,
        event_name: eventName,
        event_data: eventData,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.log('Analytics tracking failed (non-critical):', error);
    }
  };

  const trackFieldInteraction = (fieldName) => {
    trackEvent('form_field_interaction', {
      field: fieldName,
      page: 'booking_form'
    });
  };

  const eventTypes = [
    { value: 'Birthday Party', emoji: '🎂', desc: 'Kids or Adults' },
    { value: 'Wedding', emoji: '💒', desc: 'Reception Entertainment' },
    { value: 'Corporate Event', emoji: '🏢', desc: 'Team Building & Keynotes' },
    { value: 'School Event', emoji: '🏫', desc: 'Educational Entertainment' },
    { value: 'Community Event', emoji: '🎪', desc: 'Local Celebrations' },
    { value: 'Christmas Party', emoji: '🎅', desc: 'Festive Fun' },
    { value: 'Punch & Judy', emoji: '🎭', desc: 'Traditional or Modern' },
    { value: 'Comedy Magic Show', emoji: '✨', desc: 'Laughs & Wonder' },
    { value: 'Hypnotist Show', emoji: '😵', desc: 'Safe & Fun (No actual hypnosis!)' },
    { value: 'Other', emoji: '🎉', desc: 'Tell us what you need' }
  ];

  const handleSubmit = async () => {
    if (!formData.clientName || !formData.email || !formData.eventDate || !formData.eventType) {
      setStatus('error');
      setErrorMessage('Please fill in all required fields (Name, Email, Date, Event Type)');
      trackEvent('form_validation_error', { missing_fields: true });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address');
      trackEvent('form_validation_error', { invalid_email: true });
      return;
    }

    setStatus('loading');
    setErrorMessage('');
    trackEvent('form_submit_attempt', { event_type: formData.eventType });

    try {
      const { data, error } = await supabase
        .from('bookings')
        .insert([{
          user_id: CHRIS_USER_ID,
          client_name: formData.clientName,
          email: formData.email,
          phone: formData.phone || '',
          event_date: formData.eventDate,
          event_type: formData.eventType,
          venue: formData.venue || '',
          fee: parseFloat(formData.estimatedBudget) || 0,
          status: 'pending',
          notes: formData.notes || ''
        }]);

      if (error) throw error;

      setStatus('success');
      trackEvent('booking_submitted_success', {
        event_type: formData.eventType,
        has_budget: !!formData.estimatedBudget,
        has_venue: !!formData.venue,
        has_notes: !!formData.notes
      });
      
      setTimeout(() => {
        setFormData({
          clientName: '',
          email: '',
          phone: '',
          eventDate: '',
          eventType: '',
          venue: '',
          estimatedBudget: '',
          notes: ''
        });
        setStatus('idle');
      }, 5000);

    } catch (error) {
      console.error('Error submitting booking:', error);
      setStatus('error');
      setErrorMessage('Oops! Something went wrong. Please try again or contact us directly.');
      trackEvent('booking_submission_error', { error: error.message });
    }
  };

  if (status === 'success') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #9333ea, #ec4899, #f97316)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '64px 48px',
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <CheckCircle size={80} color="#22c55e" style={{ margin: '0 auto 24px' }} />
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '16px', color: '#111827' }}>
            Booking Request Received! 🎉
          </h1>
          <p style={{ fontSize: '18px', color: '#6b7280', marginBottom: '24px' }}>
            Thank you, <strong>{formData.clientName}</strong>! Chris will be in touch shortly to discuss your {formData.eventType}.
          </p>
          <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '32px' }}>
            Check your email at <strong>{formData.email}</strong> for confirmation.
          </p>
          <div style={{
            background: '#f9fafb',
            padding: '24px',
            borderRadius: '12px',
            marginBottom: '24px'
          }}>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px' }}>
              🎪 Meanwhile, check out Chris's other projects:
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="https://chrisptee-entertainments.netlify.app" onClick={() => trackEvent('link_click', { link: 'website' })} style={{
                padding: '8px 16px',
                background: 'linear-gradient(to right, #9333ea, #ec4899)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Visit Website
              </a>
              <a href="https://spicylister.com" onClick={() => trackEvent('link_click', { link: 'spicylister' })} style={{
                padding: '8px 16px',
                background: '#f97316',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Try SpicyLister
              </a>
              <a href="https://buymeacoffee.com/chrispteemagician" onClick={() => trackEvent('link_click', { link: 'coffee' })} style={{
                padding: '8px 16px',
                background: '#FFDD00',
                color: '#000',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                ☕ Buy Me a Coffee
              </a>
            </div>
          </div>
          <button
            onClick={() => {
              setStatus('idle');
              trackEvent('book_another_clicked');
            }}
            style={{
              padding: '12px 24px',
              background: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              color: '#6b7280'
            }}
          >
            Book Another Event
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #9333ea, #ec4899, #f97316)',
      padding: '24px'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        <div style={{
          textAlign: 'center',
          color: 'white',
          marginBottom: '32px',
          padding: '32px 0'
        }}>
          <h1 style={{
            fontSize: '48px',
            fontWeight: 'bold',
            marginBottom: '16px',
            textShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}>
            🎪 Book Chris P Tee
          </h1>
          <p style={{ fontSize: '20px', opacity: 0.95, marginBottom: '8px' }}>
            Magic Circle Comedy Magician • 30+ Years Experience
          </p>
          <p style={{ fontSize: '16px', opacity: 0.9 }}>
            From Weddings to Corporate Events • Punch & Judy to Comedy Magic
          </p>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '48px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px', color: '#111827' }}>
              Request a Booking
            </h2>
            <p style={{ color: '#6b7280' }}>
              Fill in the details below and Chris will get back to you within 24 hours!
            </p>
          </div>

          {status === 'error' && (
            <div style={{
              background: '#fef2f2',
              border: '2px solid #fca5a5',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              display: 'flex',
              gap: '12px',
              alignItems: 'start'
            }}>
              <AlertCircle size={20} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ color: '#dc2626', fontSize: '14px', margin: 0 }}>
                {errorMessage}
              </p>
            </div>
          )}

          <div style={{ display: 'grid', gap: '24px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <User size={20} color="#9333ea" />
                Your Details
              </h3>
              
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                    onFocus={() => trackFieldInteraction('client_name')}
                    placeholder="John Smith"
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                      Email Address *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={18} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        onFocus={() => trackFieldInteraction('email')}
                        placeholder="john@example.com"
                        style={{
                          width: '100%',
                          padding: '14px 14px 14px 44px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '16px'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                      Phone Number
                    </label>
                    <div style={{ position: 'relative' }}>
                      <Phone size={18} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        onFocus={() => trackFieldInteraction('phone')}
                        placeholder="07976 884254"
                        style={{
                          width: '100%',
                          padding: '14px 14px 14px 44px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '16px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={20} color="#9333ea" />
                Event Details
              </h3>

              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                    Event Type *
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                    {eventTypes.map(type => (
                      <button
                        key={type.value}
                        onClick={() => {
                          setFormData({...formData, eventType: type.value});
                          trackEvent('event_type_selected', { event_type: type.value });
                        }}
                        style={{
                          padding: '12px',
                          border: formData.eventType === type.value ? '3px solid #9333ea' : '2px solid #e5e7eb',
                          borderRadius: '10px',
                          background: formData.eventType === type.value ? '#f3e8ff' : 'white',
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ fontSize: '24px', marginBottom: '4px' }}>{type.emoji}</div>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#111827' }}>{type.value}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{type.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                      Event Date *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <Calendar size={18} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                      <input
                        type="date"
                        value={formData.eventDate}
                        onChange={(e) => setFormData({...formData, eventDate: e.target.value})}
                        onFocus={() => trackFieldInteraction('event_date')}
                        min={new Date().toISOString().split('T')[0]}
                        style={{
                          width: '100%',
                          padding: '14px 14px 14px 44px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '16px'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                      Estimated Budget
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontWeight: 'bold', color: '#9ca3af', fontSize: '18px' }}>£</span>
                      <input
                        type="number"
                        value={formData.estimatedBudget}
                        onChange={(e) => setFormData({...formData, estimatedBudget: e.target.value})}
                        onFocus={() => trackFieldInteraction('budget')}
                        placeholder="350"
                        style={{
                          width: '100%',
                          padding: '14px 14px 14px 44px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '16px'
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                    Venue / Location
                  </label>
                  <div style={{ position: 'relative' }}>
                    <MapPin size={18} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '14px' }} />
                    <input
                      type="text"
                      value={formData.venue}
                      onChange={(e) => setFormData({...formData, venue: e.target.value})}
                      onFocus={() => trackFieldInteraction('venue')}
                      placeholder="Happy Days Community Centre, Manchester"
                      style={{
                        width: '100%',
                        padding: '14px 14px 14px 44px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '16px'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                    Additional Details
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    onFocus={() => trackFieldInteraction('notes')}
                    placeholder="Tell Chris about your event... number of guests, ages, special requirements, etc."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={status === 'loading'}
              style={{
                width: '100%',
                padding: '18px',
                background: status === 'loading' ? '#d1d5db' : 'linear-gradient(to right, #9333ea, #ec4899)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                marginTop: '8px'
              }}
            >
              {status === 'loading' ? 'Sending...' : '✨ Request Booking'}
            </button>

            <p style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
              Chris will review your request and get back to you within 24 hours
            </p>
          </div>
        </div>

        <div style={{
          textAlign: 'center',
          color: 'white',
          marginTop: '32px',
          padding: '24px',
          opacity: 0.9
        }}>
          <p style={{ marginBottom: '12px', fontSize: '16px' }}>
            Made with ❤️ by <a href="http://chrisptee.co.uk" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'underline', fontWeight: 'bold' }}>Chris P Tee Magician</a>
          </p>
          <p style={{ fontSize: '14px' }}>
            Please support the <a href="http://comedymagic.co.uk" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'underline' }}>Community Comedy Magic Tour</a> 🎪
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicBookingForm;